import { type ReactNode, useEffect } from 'react';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { errorTracker } from '@repo/shared/common';
import { AlertCircle } from '~/components/icons';
import { Button, EmptyState } from '~/components/ui';

/**
 * Route-level error boundary — the router's `defaultErrorComponent`. Catches a
 * loader / beforeLoad / render failure for the matched route, reports it once,
 * and offers a retry (`reset` re-runs the route). Replaces the ad-hoc
 * react-error-boundary that used to wrap the Outlet; the root boundary in
 * `__root` remains as the outer net for catastrophic shell errors.
 */
export function RouteError({ error, reset }: ErrorComponentProps): ReactNode {
  useEffect(() => {
    errorTracker.captureException(
      error instanceof Error ? error : new Error(String(error)),
      { source: 'routeErrorComponent' },
    );
  }, [error]);

  return (
    <EmptyState
      data-testid="route-error"
      icon={<AlertCircle size={48} />}
      title="Something went wrong"
      description={
        error instanceof Error ? error.message : 'An unexpected error occurred.'
      }
      actions={
        <Button onClick={reset} data-testid="route-error-retry">
          Try again
        </Button>
      }
    />
  );
}
